"use client";

/**
 * The deck (composer panels, spec 2026-09-19 §3.4): every panel of the turn,
 * by kind, in the story's type scale (`ts-root` carries the ts-* variables
 * the shell reads). Escape closes the top DRAWN panel, claimed in the
 * window's capture phase like the thread's and the crown's own Escape, so one
 * press never closes two things: the deck yields to a press already claimed,
 * and the thread yields to the deck's.
 *
 * The invariant hosts rely on: the deck claims Escape for the TOP drawn panel
 * on the window in the capture phase and calls preventDefault, and it yields
 * when the press was already prevented. PillThread, ComposerInlineCard and
 * LineWork yield to a prevented Escape too, so registration order never
 * decides who closes.
 */
import { useEffect } from "react";
import type {
  ComposerPanel,
  ComposerPanelKind,
  ComposerUndoResponse,
} from "@bash/shared";
import { DRAWN_KINDS, panelKey } from "./drawn";
import { PanelCard } from "./PanelCard";

export interface PanelDeckProps {
  panels: ComposerPanel[] | undefined;
  onClose: (kind: ComposerPanelKind) => void;
  onOpen: (route: string) => void;
  onUndo: (runId: string) => Promise<ComposerUndoResponse>;
  words?: boolean;
}

export function PanelDeck({
  panels,
  onClose,
  onOpen,
  onUndo,
  words,
}: PanelDeckProps) {
  const list = panels ?? [];
  const top = list.filter((p) => DRAWN_KINDS.has(p.kind)).at(-1) ?? null;
  const topKind = top?.kind ?? null;
  useEffect(() => {
    if (!topKind) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onClose(topKind);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [topKind, onClose]);
  if (list.length === 0) return null;
  return (
    <div className="ts-root grid gap-2" data-testid="panel-deck">
      {list.map((p) => (
        <PanelCard
          key={panelKey(p)}
          panel={p}
          onClose={() => onClose(p.kind)}
          onOpen={onOpen}
          onUndo={onUndo}
          words={words}
        />
      ))}
    </div>
  );
}
